// api/_utils/archetypeVersionConstants.js
//
// Archetype Architecture Phase 2 (P2.1) — the version constants the Phase 1
// Master Spec V1.2 introduces (§4.3, §1.2, §1.3, §2.3). Constants only; the
// modules that give each one teeth (content hashes + change-without-bump CI
// locks) live beside them (calibrationBundle.js, gameModePolicy.js,
// archetypeRegistry.js). Nothing in production imports this module in Phase 2
// — every consumer arrives with the dark compiler plumbing.
//
// Bump discipline (Spec §4.3 / §2.3): each constant is MONOTONIC and bumps
// when its covered content changes. The paired hash-lock tests fail the build
// on content-change-without-version-bump, so "bumped on any table change" is
// enforced, not aspirational.

// §4.3 — one monotonic version covering every live physics table:
//   - hftConfig knob table (api/_utils/agentArchetypeConfig.js, KNOB_CONFIG_VERSION 2)
//   - ARCHETYPE_WEIGHTS / ARCHETYPE_TEMPERATURES / ARCHETYPE_CONSTRAINTS
//     (api/_utils/archetypeScoring.js)
//   - strategy-preset levers (api/_utils/agentPresetConfig.js PRESET_CONFIGS)
//   - tempo dial bands (api/_utils/tempoDialBands.js TEMPO_DIAL_BANDS)
// Coverage is composed + hashed in calibrationBundle.js.
export const CALIBRATION_BUNDLE_VERSION = 1;

// §4.3 / Spec §3.2 — versions the Forge rule corpus (the "baseline rulebook"):
// src/data/forgeKnowledgeBase.js FORGE_RULE_TEMPLATES (+ conflict pairs).
// Stamped into sourceRevisionVector.ruleLibraryVersion at compile.
export const RULE_LIBRARY_VERSION = 1;

// §4.3 — versions the prompt-template generation (agentPromptAssembly /
// agentEvalPromptAssembly rendering logic, which resolves live at tick per
// census Map 5). Stamped into effectiveRuntimeResolution.promptSpecVersion
// when the tick-side envelope capture lands (P2.6).
export const PROMPT_SPEC_VERSION = 1;

// §1.2 — versions the PlatformGuardrails contract (platformGuardrails.js).
// Bumped on any change to the platform floors / universal filters /
// sector-cap policy the contract documents.
// v2 (2026-07-23, Sector Cap Activation): SECTOR_CAP_MODE 'true' → 'observe' and
// the sectorCapPolicy capSource (now config-derived, no longer "hardcoded 35")
// both changed the §1.2 contract content — bumped here and RECORDED_PLATFORM_HASH
// re-recorded in the same commit (archetypePhase2Constants.test.js).
export const GUARDRAIL_SET_VERSION = 2;

// §1.3 / A-2 — versions the GameModePolicy table (gameModePolicy.js). A-2
// additionally gives each mode's policy a content hash; both enter
// CompiledBuild.contentHash AND sourceRevisionVector.
export const GAME_MODE_POLICY_VERSION = 1;

// §2.3 / §3.3 — the registry identity version. Every published
// identityVersion emits an immutable snapshot artifact
// (docs/registry-snapshots/), and the registry CI lock fails on
// content-change-without-bump. Builds compile against their EXACT
// parentIdentityVersion (§3.3 — missing/retired versions fail explicitly).
// v2 (Aug 6 2026, composition PR 3.5 — founder trait ruling): the three
// default-trait ladders re-authored inside their adjudicated domains
// (patient-holder/mb-01 minutes 30/45/60; dual-conviction/tv-10 fund_score
// 71/75/80; trend-rider/tv-01 stretched 80/77/75). v1 snapshot immutable.
export const ARCHETYPE_IDENTITY_VERSION = 2;

// §4.4 — stamped into every CompiledBuild as compilerVersion. Bumped when
// compileBuild's derivation/merge/legality semantics change.
export const COMPILER_VERSION = 1;

// §8 + R1 finding 2 — stamped into every ResolvedAgentManifest at creation.
// Battles keep their BIRTH policy: mutation endpoints consult the battle's
// stamp, never the global constant, so a freeze-policy change can never
// retro-apply to in-flight battles. Bumped when the §8 freeze policy
// (which layers freeze at lock vs resolve live) changes.
export const FREEZE_POLICY_VERSION = 1;
